import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Clock, Target, Rocket, BookOpen, ExternalLink } from 'lucide-react';
import ReactionBar from './ReactionBar';

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function AvatarStack({ avatars = [], count }) {
  return (
    <div className="flex items-center">
      {avatars.slice(0, 4).map((a, i) => <img key={i} src={a} alt="" className="w-5 h-5 rounded-full border-2 border-white -ml-1 first:ml-0" />)}
      {count > 4 && <span className="text-xs text-slate-400 ml-1.5">+{count - 4}</span>}
    </div>
  );
}

function VerifiedBadge() {
  return <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600"><ShieldCheck className="w-3 h-3" /></span>;
}

function PostHeader({ post }) {
  const link = post.authorType === 'ngo' ? `/ngo/${post.ngoSlug}` : `/profile/${post.authorName?.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="flex items-start gap-3 mb-3">
      <Link to={link}><img src={post.authorAvatar} alt="" className="w-10 h-10 rounded-full object-cover" /></Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Link to={link} className="font-semibold text-sm text-slate-900 hover:underline">{post.authorName}</Link>
          {post.authorVerified && <VerifiedBadge />}
        </div>
        <p className="text-xs text-slate-400">{timeAgo(post.timestamp)}</p>
      </div>
    </div>
  );
}

function NGOUpdateCard({ post }) {
  return (
    <div>
      <PostHeader post={post} />
      <p className="text-sm text-slate-700 leading-relaxed mb-3">{post.content}</p>
      {post.images?.[0] && <Link to={`/project/${post.projectSlug}`}><img src={post.images[0]} alt="" className="w-full rounded-lg object-cover max-h-80 mb-3" /></Link>}
      {post.metrics && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 mb-3 border border-emerald-100">
          <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
          <span className="text-xs font-medium text-emerald-700">{post.metrics.label}</span>
          <span className="text-[10px] text-emerald-500">Verified</span>
        </div>
      )}
      {post.donorCount > 0 && (
        <div className="flex items-center gap-2 mb-1">
          <AvatarStack avatars={post.donorAvatars} count={post.donorCount} />
          <span className="text-xs text-slate-400">Funded by {post.donorCount} donors</span>
        </div>
      )}
      <ReactionBar data={post.reactions} postId={post.id} txHash={post.txHash} projectTitle={post.projectTitle} projectSlug={post.projectSlug} commentsCount={post.commentsCount} />
    </div>
  );
}

function DonationMilestoneCard({ post }) {
  return (
    <div>
      <PostHeader post={post} />
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-2xl font-bold text-[#7C3AED]">${post.amount?.toLocaleString()}</span>
        <span className="text-sm text-slate-500">donated to</span>
        <Link to={`/project/${post.projectSlug}`} className="text-sm font-medium text-slate-900 hover:underline">{post.projectTitle}</Link>
      </div>
      {post.content && <p className="text-sm text-slate-500 leading-relaxed mb-3 italic">"{post.content}"</p>}
      <Link to={`/project/${post.projectSlug}`} className="block rounded-lg border border-slate-200 overflow-hidden mb-3 hover:border-slate-300 transition-colors">
        <div className="flex items-center gap-3 p-3">
          {post.images?.[0] && <img src={post.images[0]} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{post.projectTitle}</p>
            <div className="w-full h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB]" style={{ width: `${post.projectProgress}%` }} /></div>
            <p className="text-xs text-slate-400 mt-1">{post.projectProgress}% funded</p>
          </div>
        </div>
      </Link>
      <a href={`https://bscscan.com/tx/${post.txHash}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#7C3AED] hover:underline flex items-center gap-1 mb-1">View receipt on-chain <ExternalLink className="w-3 h-3" /></a>
      <ReactionBar data={post.reactions} postId={post.id} txHash={post.txHash} projectTitle={post.projectTitle} projectSlug={post.projectSlug} commentsCount={post.commentsCount} />
    </div>
  );
}

function ProjectMilestoneCard({ post }) {
  return (
    <div>
      <div className="flex items-start gap-3 mb-3">
        <Link to={`/ngo/${post.ngoSlug}`}><img src={post.authorAvatar} alt="" className="w-10 h-10 rounded-full object-cover" /></Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to={`/ngo/${post.ngoSlug}`} className="font-semibold text-sm text-slate-900 hover:underline">{post.authorName}</Link>
            <span className="text-xs text-slate-400">reached a milestone</span>
            <Target className="w-3.5 h-3.5 text-amber-500" />
          </div>
          <p className="text-xs text-slate-400">{timeAgo(post.timestamp)}</p>
        </div>
      </div>
      <p className="text-sm font-medium text-slate-900 mb-3">{post.content}</p>
      <div className="relative h-3 rounded-full bg-slate-100 overflow-hidden mb-2">
        <motion.div initial={{ width: 0 }} animate={{ width: `${post.progress}%` }} transition={{ duration: 1 }} className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB]" />
      </div>
      <p className="text-xs text-slate-400 mb-3">{post.progress}% complete</p>
      {post.images?.[0] && <img src={post.images[0]} alt="" className="w-full rounded-lg object-cover max-h-56 mb-3" />}
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /><span className="text-xs text-emerald-600 font-medium">Verified</span>
        <span className="text-xs text-slate-300 font-mono">{post.txHash?.slice(0, 10)}...</span>
      </div>
      <ReactionBar data={post.reactions} postId={post.id} txHash={post.txHash} projectTitle={post.projectTitle} projectSlug={post.projectSlug} commentsCount={post.commentsCount} />
    </div>
  );
}

function VolunteerActivityCard({ post }) {
  return (
    <div>
      <div className="flex items-start gap-3 mb-3">
        <img src={post.authorAvatar} alt="" className="w-10 h-10 rounded-full object-cover" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-semibold text-sm text-slate-900">{post.authorName}</span>
            <span className="text-xs text-slate-400">logged</span>
            <span className="font-semibold text-sm text-[#7C3AED]">{post.hoursLogged}h</span>
            <span className="text-xs text-slate-400">with</span>
            <Link to={`/ngo/${post.ngoSlug}`} className="text-sm font-medium text-slate-900 hover:underline">{post.ngoName}</Link>
          </div>
          <p className="text-xs text-slate-400">{timeAgo(post.timestamp)}</p>
        </div>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed mb-3">{post.content}</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {post.skills?.map(s => <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">{s}</span>)}
      </div>
      <div className="flex items-center gap-2 mb-1"><Clock className="w-3.5 h-3.5 text-emerald-500" /><span className="text-xs text-emerald-600 font-medium">{post.hoursLogged} hours verified</span></div>
      <ReactionBar data={post.reactions} postId={post.id} txHash={post.txHash} commentsCount={post.commentsCount} />
    </div>
  );
}

function FundraiserLaunchCard({ post }) {
  return (
    <div>
      <div className="flex items-start gap-3 mb-3">
        <Link to={`/ngo/${post.ngoSlug}`}><img src={post.authorAvatar} alt="" className="w-10 h-10 rounded-full object-cover" /></Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to={`/ngo/${post.ngoSlug}`} className="font-semibold text-sm text-slate-900 hover:underline">{post.authorName}</Link>
            <span className="text-xs text-slate-400">launched a new project</span>
            <Rocket className="w-3.5 h-3.5 text-[#7C3AED]" />
          </div>
          <p className="text-xs text-slate-400">{timeAgo(post.timestamp)}</p>
        </div>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed mb-3">{post.content}</p>
      <Link to={`/project/${post.projectSlug}`} className="block rounded-lg border border-slate-200 overflow-hidden mb-3 hover:border-slate-300 transition-colors">
        {post.images?.[0] && <img src={post.images[0]} alt="" className="w-full h-40 object-cover" />}
        <div className="p-3">
          <p className="font-semibold text-sm text-slate-900">{post.projectTitle}</p>
          <div className="w-full h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB]" style={{ width: `${post.projectProgress}%` }} /></div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-slate-500">${post.projectFundingRaised?.toLocaleString()} of ${post.projectFundingGoal?.toLocaleString()}</span>
            <span className="text-xs font-medium text-[#7C3AED]">{post.projectProgress}%</span>
          </div>
        </div>
      </Link>
      <ReactionBar data={post.reactions} postId={post.id} txHash={post.txHash} projectTitle={post.projectTitle} projectSlug={post.projectSlug} commentsCount={post.commentsCount} />
    </div>
  );
}

function ImpactStoryCard({ post }) {
  return (
    <div>
      <PostHeader post={post} />
      <Link to="/stories" className="block relative rounded-lg overflow-hidden mb-3 group">
        {post.images?.[0] && (
          <>
            <img src={post.images[0]} alt="" className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-white font-semibold text-base mb-1">{post.storyTitle}</p>
              <div className="flex items-center gap-2"><BookOpen className="w-3.5 h-3.5 text-white/70" /><span className="text-xs text-white/70">{post.readTime}</span></div>
            </div>
          </>
        )}
      </Link>
      <ReactionBar data={post.reactions} postId={post.id} txHash={post.txHash} commentsCount={post.commentsCount} />
    </div>
  );
}

const cardMap = {
  ngo_update: NGOUpdateCard,
  donation_milestone: DonationMilestoneCard,
  project_milestone: ProjectMilestoneCard,
  volunteer_activity: VolunteerActivityCard,
  fundraiser_launch: FundraiserLaunchCard,
  impact_story: ImpactStoryCard,
};

export default function FeedCard({ post, index }) {
  const Card = cardMap[post.type];
  if (!Card) return null;
  return (
    <motion.article initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.2) }} className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 relative" data-testid={`feed-card-${post.id}`}>
      <Card post={post} />
    </motion.article>
  );
}
